import { useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import {
  closeSecondaryToolbarMenuSetter,
  Service,
  takeSnapshotSetter,
  determineAssetType,
} from "@zapier/canvas-state";
import { App, AppPicker } from "@zapier/app-picker";

import useUpdateNode from "../../../hooks/useUpdateNode";
import useServiceById from "../../../hooks/useServiceById";
import { AppChangeWarning } from "../../AppChangeWarning";
import { NodeToolbarTop } from "../NodeToolbarTop";

type Props = {
  selectedApps?: Service[];
  isZapNode: boolean;
};

export function FloatingAppTypeahead({ selectedApps, isZapNode }: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();

  const getService = useServiceById();
  const appId = selectedApps?.[0]?.currentImplementation.id ?? "";
  const service = getService(appId);

  const hasApp = selectedApps && selectedApps.length > 0;
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [newSelectedApp, setNewSelectedApp] = useState<App | null>(null);
  const [isRemovingApp, setIsRemovingApp] = useState<boolean>(false);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    setNewSelectedApp(null);
    setIsRemovingApp(false);
  }, []);

  const handleAppChange = useCallback(
    (app: App) => {
      takeSnapshot();

      const newAssetType =
        app.name === "Zap"
          ? "zap"
          : determineAssetType(
              [
                {
                  currentImplementation: {
                    id: app.currentImplementation.id,
                  },
                },
              ],
              "default",
            );

      if (newAssetType === "zap") {
        updateNodeAndSave({
          type: "zap",
          data: {
            color: undefined, // Clear color in order to fall back to default zap node colors
            ...(hasApp ? { link: undefined, notesJson: undefined } : {}), // Only clear these when changing apps, not when adding an app for the first time
          },
        });
      } else {
        updateNodeAndSave({
          type: "default",
          data: {
            app: app.name,
            services: [
              {
                currentImplementation: {
                  id: app.currentImplementation.id,
                },
              },
            ],
            ...(newAssetType ? { color: undefined } : {}), // Clear color in order to fall back to default asset node colors
            ...(hasApp ? { link: undefined, notesJson: undefined } : {}), // Only clear these when changing apps, not when adding an app for the first time
          },
        });
      }

      dismissWarning();
      closeSecondaryMenu();
    },
    [
      hasApp,
      dismissWarning,
      takeSnapshot,
      updateNodeAndSave,
      closeSecondaryMenu,
    ],
  );

  const onSelectionChange = useCallback(
    (app: App) => {
      // Show warning only when changing apps (not on first app selection) since there's nothing to reset on first selection
      if (hasApp) {
        setNewSelectedApp(app);
        setShowWarning(true);
      } else {
        handleAppChange(app);
      }
    },
    [handleAppChange, hasApp],
  );

  const handleRemoveApp = useCallback(() => {
    takeSnapshot();

    if (isZapNode) {
      updateNodeAndSave({
        type: "default",
        data: {
          link: undefined,
          notesJson: undefined,
        },
      });
    } else {
      updateNodeAndSave({
        data: {
          app: undefined,
          services: undefined,
          tableId: undefined,
          projectId: undefined,
          pageId: undefined,
          chatbotId: undefined,
          link: undefined,
          notesJson: undefined,
        },
      });

      dismissWarning();
    }
  }, [dismissWarning, isZapNode, takeSnapshot, updateNodeAndSave]);

  const onRemoveApp = useCallback(() => {
    setIsRemovingApp(true);
    setShowWarning(true);
  }, []);

  return (
    <NodeToolbarTop>
      {showWarning && (
        <AppChangeWarning
          onConfirm={
            isRemovingApp
              ? handleRemoveApp
              : () => handleAppChange(newSelectedApp!)
          }
          onDismiss={dismissWarning}
          isRemovingApp={isRemovingApp}
        />
      )}
      <AppPicker
        onSelect={onSelectionChange}
        onClose={closeSecondaryMenu}
        selectedApp={isZapNode ? "Zap" : (service?.name ?? "")}
        onRemove={isZapNode || hasApp ? onRemoveApp : undefined}
        isDarkMode={true}
        shouldCloseOnClickOutside={false}
      />
    </NodeToolbarTop>
  );
}
